import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Divider,
  useTheme,
  alpha,
  Tooltip,
  useMediaQuery,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Badge,
  CircularProgress,
  Alert,
} from '@mui/material';
// Removed MUI X date pickers - using native HTML inputs instead
import toast from 'react-hot-toast';
import { MeetingService } from '../../services/meetingService';
import { supabase } from '../../lib/supabase';
import {
  Close as CloseIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  Place as PlaceIcon,
  VideoCall as VideoIcon,
  Link as LinkIcon,
  Notes as NotesIcon,
  CheckCircle as CheckIcon,
  EditCalendar as EditCalendarIcon,
  Send as SendIcon,
  Save as SaveIcon,
  Public as PublicIcon,
  Phone as PhoneIcon,
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  TableChart as ExcelIcon,
  Slideshow as PowerpointIcon,
  TextSnippet as TextIcon,
  AttachFile as AttachFileIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';

interface UserDocument {
  id: string;
  user_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  document_type: string;
  description?: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  url?: string;
}

interface ManageInterviewModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (data: any) => void;
  onMeetingCreated?: () => void; // Callback to refresh application data
  application?: {
    id: string;
    product?: { name?: string };
    applicant?: { id?: string; first_name?: string; last_name?: string; alias?: string };
  };
}

const ManageInterviewModal: React.FC<ManageInterviewModalProps> = ({ open, onClose, onSave, onMeetingCreated, application }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [interviewType, setInterviewType] = useState<'in_person' | 'video' | 'phone'>('video');
  const [date, setDate] = useState<string>('');
  const [time, setTime] = useState<string>('');
  const [timezone, setTimezone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [timezoneDialogOpen, setTimezoneDialogOpen] = useState(false);
  const [timezoneSearch, setTimezoneSearch] = useState('');
  const [location, setLocation] = useState<string>('');
  const [videoLink, setVideoLink] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  
  // Document selection state
  const [userDocuments, setUserDocuments] = useState<UserDocument[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showDocumentSelector, setShowDocumentSelector] = useState(false);

  const applicantLabel = useMemo(() => {
    if (!application) return '';
    const name = `${application.applicant?.first_name || ''} ${application.applicant?.last_name || ''}`.trim();
    if (name) return name;
    if (application.applicant?.alias) return `@${application.applicant.alias}`;
    return 'Candidate';
  }, [application]);

  const isFormValid = () => {
    const baseOk = !!(date && time && timezone && interviewType);
    if (!baseOk) return false;
    if (interviewType === 'in_person') return !!location.trim();
    if (interviewType === 'video') return /^https?:\/\//i.test(videoLink || '');
    if (interviewType === 'phone') return /[+\d][\d\s().-]{5,}/.test(phoneNumber || '');
    return true; // phone
  };

  // Fetch user documents
  const fetchUserDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', user.id)
        .is('company_id', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching documents:', error);
        return;
      }

      setUserDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Load documents when modal opens
  useEffect(() => {
    if (open) {
      fetchUserDocuments();
    }
  }, [open]);

  const handleSend = async () => {
    console.log('🔍 ManageInterviewModal: Selected documents:', selectedDocuments);
    console.log('📄 User documents available:', userDocuments.map(d => ({ id: d.id, name: d.file_name })));

    const payload = {
      interviewType,
      date: date || null,
      time: time || null,
      timezone,
      location,
      videoLink,
      notes,
      documents: selectedDocuments,
    };
    onSave?.(payload);

    // Persist to backend meetings table
    try {
      if (!isFormValid()) {
        throw new Error('Please select type, date, time, timezone and required fields.');
      }
      if (!application?.id) throw new Error('Missing application id');
      const participantId = application?.applicant?.id;
      if (!participantId) throw new Error('Missing candidate user id');
      if (!date || !time) throw new Error('Please select date and time');

      console.log('🚀 ManageInterviewModal: Creating meeting with documents:', selectedDocuments);

      await MeetingService.createMeeting({
        applicationId: application.id,
        participantUserId: participantId,
        interviewType,
        timezone,
        localDateISO: date,
        localTimeHHmm: time,
        locationAddress: interviewType === 'in_person' ? location : undefined,
        meetingLink: interviewType === 'video' ? videoLink : undefined,
        phoneNumber: interviewType === 'phone' ? phoneNumber : undefined,
        notes,
        documents: selectedDocuments,
      });
      toast.success(`Meeting created with ${selectedDocuments.length} document${selectedDocuments.length !== 1 ? 's' : ''}`);
      
      // Refresh application data to show the new meeting
      onMeetingCreated?.();
      onClose();
    } catch (err: any) {
      console.error('❌ ManageInterviewModal: Error creating meeting:', err);
      toast.error(err?.message || 'Failed to create meeting');
    }
  };

  const handleSaveDraft = () => {
    // For now we keep draft local and just close with a toast (no backend insert required fields)
    toast.success('Draft saved');
    onClose();
  };

  // Document handling functions
  const toggleDocumentSelection = (documentId: string) => {
    setSelectedDocuments(prev => 
      prev.includes(documentId) 
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <PdfIcon sx={{ color: '#d32f2f' }} />;
    if (fileType.includes('word') || fileType.includes('document')) return <DocumentIcon sx={{ color: '#1976d2' }} />;
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return <ExcelIcon sx={{ color: '#388e3c' }} />;
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return <PowerpointIcon sx={{ color: '#f57c00' }} />;
    if (fileType.includes('image')) return <ImageIcon sx={{ color: '#7b1fa2' }} />;
    if (fileType.includes('text')) return <TextIcon sx={{ color: '#5d4037' }} />;
    return <DocumentIcon sx={{ color: '#757575' }} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Quickly generate a realistic meeting setup for demos/testing
  const generateSampleMeeting = () => {
    const types: Array<'video' | 'in_person' | 'phone'> = ['video', 'in_person', 'phone'];
    const chosenType = types[Math.floor(Math.random() * types.length)];

    const tz = timezones[Math.floor(Math.random() * timezones.length)] || timezone;
    setTimezone(tz);

    // Pick a date within the next 30 days, and time between 9:00 and 17:30
    const daysAhead = Math.floor(Math.random() * 30) + 1;
    const base = new Date();
    base.setDate(base.getDate() + daysAhead);
    const hour = 9 + Math.floor(Math.random() * 9); // 9..17
    const minute = [0, 15, 30, 45][Math.floor(Math.random() * 4)];
    setDate(base.toISOString().split('T')[0]); // YYYY-MM-DD format
    setTime(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`); // HH:mm format

    const offices = [
      '123 Main St, London',
      'Karl-Liebknecht-Str. 1, Berlin',
      '1 Market St, San Francisco',
      'Shinjuku, Tokyo',
      'Marina Bay, Singapore',
    ];
    const videoVendors = [
      () => `https://meet.google.com/${Math.random().toString(36).slice(2,5)}-${Math.random().toString(36).slice(2,6)}-${Math.random().toString(36).slice(2,3)}${Math.floor(Math.random()*9)}`,
      () => `https://zoom.us/j/${Math.floor(1_000_000_000 + Math.random()*9_000_000_000)}`,
      () => `https://teams.microsoft.com/l/meetup-join/${Math.random().toString(36).slice(2,15)}`,
    ];

    const sampleNotes = [
      'Panel: Hiring Manager + Frontend Lead. Bring portfolio and be ready for a 15‑min live coding exercise.',
      'We will discuss your recent projects and system design decisions. Please prepare a short walkthrough.',
      'Culture add interview with HR and Team Lead. No coding, just experiences and collaboration style.',
      'Please ensure a stable connection and a quiet space. We will screen share for a quick demo.',
    ];

    setInterviewType(chosenType);
    if (chosenType === 'in_person') {
      setLocation(offices[Math.floor(Math.random() * offices.length)]);
      setVideoLink('');
      setPhoneNumber('');
    } else if (chosenType === 'video') {
      setVideoLink(videoVendors[Math.floor(Math.random()*videoVendors.length)]());
      setLocation('');
      setPhoneNumber('');
    } else {
      setLocation('');
      setVideoLink('');
      const phones = ['+1 415 555 0165', '+44 20 7946 0123', '+49 30 901820', '+81 3-6384-9000'];
      setPhoneNumber(phones[Math.floor(Math.random()*phones.length)]);
    }

    setNotes(sampleNotes[Math.floor(Math.random() * sampleNotes.length)]);
  };

  // Build a rich timezone list and helpers
  const timezones: string[] = useMemo(() => {
    const common = [
      'UTC',
      'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Rome', 'Europe/Madrid', 'Europe/Amsterdam',
      'Europe/Stockholm', 'Europe/Oslo', 'Europe/Helsinki', 'Europe/Warsaw', 'Europe/Istanbul',
      'Africa/Cairo', 'Africa/Johannesburg',
      'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Dhaka', 'Asia/Bangkok', 'Asia/Jakarta',
      'Asia/Singapore', 'Asia/Kuala_Lumpur', 'Asia/Hong_Kong', 'Asia/Tokyo', 'Asia/Seoul',
      'Australia/Sydney', 'Pacific/Auckland',
      'America/St_Johns', 'America/Halifax', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix',
      'America/Mexico_City', 'America/Bogota', 'America/Lima', 'America/Sao_Paulo', 'America/Argentina/Buenos_Aires'
    ];
    try {
      const supported = (Intl as any).supportedValuesOf ? (Intl as any).supportedValuesOf('timeZone') as string[] : [];
      return Array.from(new Set([...common, ...supported]));
    } catch {
      return common;
    }
  }, []);

  const filteredTimezones = useMemo(() => {
    const q = timezoneSearch.trim().toLowerCase();
    if (!q) return timezones;
    return timezones.filter(z => z.toLowerCase().includes(q));
  }, [timezoneSearch, timezones]);

  const formatNowInZone = (zone: string) => {
    try {
      return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: zone });
    } catch {
      return '';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile} PaperProps={{
      sx: {
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: `0 24px 60px ${alpha('#000', 0.2)}`,
      },
    }}>
      <DialogTitle sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Manage next steps
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {application?.product?.name || 'Job'} • {applicantLabel}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 0 }}>
        <Box sx={{
          p: 2.5,
          borderRadius: 2,
          mb: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Interview type
          </Typography>
          <ToggleButtonGroup value={interviewType} exclusive onChange={(_, v) => v && setInterviewType(v)}>
            <ToggleButton value="video" sx={{ textTransform: 'none', px: 2.5 }}>
              <VideoIcon sx={{ mr: 1 }} /> Video
            </ToggleButton>
            <ToggleButton value="in_person" sx={{ textTransform: 'none', px: 2.5 }}>
              <PlaceIcon sx={{ mr: 1 }} /> In‑person
            </ToggleButton>
            <ToggleButton value="phone" sx={{ textTransform: 'none', px: 2.5 }}>
              <TimeIcon sx={{ mr: 1 }} /> Phone
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
          <Box>
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
            />
          </Box>
          <Box>
            <TextField
              fullWidth
              label="Time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box>
            <TextField
              fullWidth
              label="Timezone"
              value={timezone}
              InputProps={{
                readOnly: true,
                startAdornment: <InputAdornment position="start"><PublicIcon sx={{ color: 'text.disabled' }} /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => setTimezoneDialogOpen(true)} sx={{ textTransform: 'none' }}>Change</Button>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {interviewType === 'in_person' && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField
                fullWidth
                label="Location address"
                placeholder="123 Main St, City, Country"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                InputProps={{ startAdornment: <PlaceIcon sx={{ mr: 1, color: 'text.disabled' }} /> as any }}
                required
                error={interviewType === 'in_person' && !location.trim()}
                helperText={interviewType === 'in_person' && !location.trim() ? 'Location is required for in-person interviews' : ' '}
              />
            </Box>
          )}

          {interviewType === 'video' && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField
                fullWidth
                label="Video meeting link"
                placeholder="https://meet.example.com/your-room"
                value={videoLink}
                onChange={(e) => setVideoLink(e.target.value)}
                InputProps={{ startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.disabled' }} /> as any }}
                required
                error={interviewType === 'video' && !/^https?:\/\//i.test(videoLink || '')}
                helperText={interviewType === 'video' && !/^https?:\/\//i.test(videoLink || '') ? 'Enter a valid http(s) link' : ' '}
              />
            </Box>
          )}

          {interviewType === 'phone' && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField
                fullWidth
                label="Phone number to call"
                placeholder="e.g., +1 415 555 0165"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.disabled' }} /> as any }}
                required
                error={interviewType === 'phone' && !(/[+\d][\d\s().-]{5,}/.test(phoneNumber || ''))}
                helperText={interviewType === 'phone' && !(/[+\d][\d\s().-]{5,}/.test(phoneNumber || '')) ? 'Enter a valid phone number' : ' '}
              />
            </Box>
          )}

          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Additional information for the candidate"
              placeholder="Agenda, interviewers, preparation tips, portfolio request, etc."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              InputProps={{ startAdornment: <NotesIcon sx={{ mr: 1, color: 'text.disabled', alignSelf: 'flex-start' }} /> as any }}
            />
          </Box>
          </Box>

        {/* Document Selection Section */}
        <Divider sx={{ my: 3 }} />
        
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                Attach Documents
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Share relevant documents with the candidate for this interview
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<AttachFileIcon />}
              onClick={() => setShowDocumentSelector(!showDocumentSelector)}
              sx={{ textTransform: 'none' }}
            >
              {showDocumentSelector ? 'Hide Documents' : 'Select Documents'}
            </Button>
          </Box>

          {/* Selected Documents Preview */}
          {selectedDocuments.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Selected Documents ({selectedDocuments.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedDocuments.map(docId => {
                  const doc = userDocuments.find(d => d.id === docId);
                  if (!doc) return null;
                  return (
                    <Chip
                      key={docId}
                      icon={getFileIcon(doc.file_type)}
                      label={doc.file_name}
                      onDelete={() => toggleDocumentSelection(docId)}
                      color="primary"
                      variant="outlined"
                      sx={{ textTransform: 'none' }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Document Selector */}
          {showDocumentSelector && (
            <Card sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
              <CardContent sx={{ p: 2 }}>
                {loadingDocuments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      Loading documents...
                    </Typography>
                  </Box>
                ) : userDocuments.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 0 }}>
                    No documents found. Upload documents in your profile to attach them to meetings.
                  </Alert>
                ) : (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                      Available Documents ({userDocuments.length})
                    </Typography>
                    <List sx={{ maxHeight: 300, overflow: 'auto', p: 0 }}>
                      {userDocuments.map((document) => (
                        <ListItem
                          key={document.id}
                          sx={{
                            border: '1px solid',
                            borderColor: selectedDocuments.includes(document.id) 
                              ? 'primary.main' 
                              : 'divider',
                            borderRadius: 1,
                            mb: 1,
                            backgroundColor: selectedDocuments.includes(document.id)
                              ? alpha(theme.palette.primary.main, 0.08)
                              : 'transparent',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.04),
                            },
                            cursor: 'pointer',
                          }}
                          onClick={() => toggleDocumentSelection(document.id)}
                        >
                          <ListItemIcon>
                            <Checkbox
                              checked={selectedDocuments.includes(document.id)}
                              onChange={() => toggleDocumentSelection(document.id)}
                              color="primary"
                            />
                          </ListItemIcon>
                          <ListItemIcon>
                            {getFileIcon(document.file_type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={500}>
                                {document.file_name}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {formatFileSize(document.file_size)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  •
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {document.document_type}
                                </Typography>
                                {document.description && (
                                  <>
                                    <Typography variant="caption" color="text.secondary">
                                      •
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {document.description}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip icon={<EditCalendarIcon />} label={date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'No date'} />
          <Chip icon={<TimeIcon />} label={time ? new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'No time'} />
          <Chip label={timezone} />
          <Chip color="primary" variant="outlined" icon={<CheckIcon />} label={interviewType.replace('_', ' ')} />
          {selectedDocuments.length > 0 && (
            <Chip 
              icon={<AttachFileIcon />} 
              label={`${selectedDocuments.length} document${selectedDocuments.length > 1 ? 's' : ''}`}
              color="secondary"
              variant="outlined"
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={generateSampleMeeting} sx={{ textTransform: 'none' }}>
            Generate example
          </Button>
          <Tooltip title="Save draft (no send)">
            <Button variant="text" startIcon={<SaveIcon />} onClick={handleSaveDraft} sx={{ textTransform: 'none' }}>
              Save draft
            </Button>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSend}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
              },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
            }}
            disabled={!isFormValid()}
          >
            Send invitation
          </Button>
        </Box>
      </DialogActions>
      {/* Timezone Picker Dialog */}
      <Dialog open={timezoneDialogOpen} onClose={() => setTimezoneDialogOpen(false)} fullScreen={isMobile} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={800}>Select timezone</Typography>
          <IconButton onClick={() => setTimezoneDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <TextField
            fullWidth
            autoFocus
            placeholder="Search city/region (e.g., Europe/Berlin)"
            value={timezoneSearch}
            onChange={(e) => setTimezoneSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><PublicIcon sx={{ color: 'text.disabled' }} /></InputAdornment> }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ maxHeight: isMobile ? 'calc(100dvh - 220px)' : 420, overflow: 'auto', pr: 1 }}>
            {filteredTimezones.map((z) => (
              <Box key={z} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box>
                  <Typography fontWeight={600}>{z.replaceAll('_', ' ')}</Typography>
                  <Typography variant="caption" color="text.secondary">Local time: {formatNowInZone(z)}</Typography>
                </Box>
                <Button variant={z === timezone ? 'contained' : 'outlined'} onClick={() => { setTimezone(z); setTimezoneDialogOpen(false); }} sx={{ textTransform: 'none' }}>
                  {z === timezone ? 'Selected' : 'Choose'}
                </Button>
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ManageInterviewModal;


